import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Button, DrawerBody, DrawerFooter, DrawerHeader, DrawerHeaderTitle, Dropdown, Field, Input, Option, OverlayDrawer, Spinner, Textarea } from '@fluentui/react-components';
import { Dismiss24Regular, Warning16Regular } from '@fluentui/react-icons';

// component functions
import * as ComponentFunctions from './PractitionerConsumption.Form.Functions';
import { buildConsumption, hasDraftErrors, parseDecimal, parseInteger, sanitizeDecimalText, sanitizeIntegerText, validateDraft } from './PractitionerConsumption.Form.Logic';

// styles
import './PractitionerConsumption.Form.Styles.css';

// sub-components
import { DateTimeInput } from '../../../../commons/component/Inputs/DateTimeInput/DateTimeInput';
import { CurrencySelectorComponent } from '../../../../commons/component/Inputs/CurrencySelector/CurrencySelectorComponent';
import ContractConceptSelectorComponent from '../../../../commons/component/Inputs/ContractConceptSelector/ContractConceptSelector.Component';
import ContractStagesSelectorComponent from '../../../../commons/component/Inputs/ContractStagesSelector/ContractStages.Selector.Component';
import ProtocolPatientSelectorComponent from '../../../../commons/component/Inputs/ProtocolPatientSelector/ProtocolPatientSelector.Component';

// models
import { ContractConcept } from '../../../../models/Entities/Contracts/ContractConcept';
import { ContractStage } from '../../../../models/Entities/Contracts/ContractStage';
import { ProtocolPatient } from '../../../../models/Entities/Protocols/ProtocolPatient';
import { Protocol } from '../../../../models/Entities/Protocols/Protocol';
import { ProtocolConsumptionRealizeTypeEnum } from '../../../../models/Enums/ProtocolConsumptionRealizeTypeEnum';
import { formatCurrencyAmount } from '../PractitionerConsumptionCurrency';

const REALIZE_TYPE_OPTIONS = [
    { key: ProtocolConsumptionRealizeTypeEnum.RealizedFaceToFace, label: 'Presencial' },
    { key: ProtocolConsumptionRealizeTypeEnum.RealizedRemotely, label: 'Remota' },
    { key: ProtocolConsumptionRealizeTypeEnum.RealizedAtHome, label: 'Domicilio' },
    { key: ProtocolConsumptionRealizeTypeEnum.NotRealized, label: 'No realizada' },
];

/**
 * Alta manual de un consumo del médico. El cuerpo lo arma
 * PractitionerConsumption.Form.Logic con lo que exige la API; acá sólo viven
 * los campos y la validación visible.
 */
const PractitionerConsumptionFormComponent = (props: ComponentFunctions.Props) => {

    const [submitted, setSubmitted] = useState(false);
    const [wasSaving, setWasSaving] = useState(false);

    const [protocol, setProtocol] = useState<Protocol | undefined>();
    const [patient, setPatient] = useState<ProtocolPatient | undefined>();
    const [stage, setStage] = useState<ContractStage | undefined>();
    const [concept, setConcept] = useState<ContractConcept | undefined>();
    const [serviceName, setServiceName] = useState('');
    const [serviceDueDate, setServiceDueDate] = useState<Date | undefined>(undefined);
    const [realizedType, setRealizedType] = useState<number>(ProtocolConsumptionRealizeTypeEnum.RealizedFaceToFace);
    const [currencyCode, setCurrencyCode] = useState('');
    const [quantityText, setQuantityText] = useState('1');
    const [unitAmountText, setUnitAmountText] = useState('0');
    const [comments, setComments] = useState('');

    const quantity = parseInteger(quantityText);
    const unitAmount = parseDecimal(unitAmountText);
    const amount = Number.isFinite(quantity) && Number.isFinite(unitAmount) ? Math.round(quantity * unitAmount * 100) / 100 : 0;

    const contractId = protocol?.contractAssociation?.contractId;

    const resetForm = () => {
        setSubmitted(false);
        setProtocol(undefined);
        setPatient(undefined);
        setStage(undefined);
        setConcept(undefined);
        setServiceName('');
        setServiceDueDate(undefined);
        setRealizedType(ProtocolConsumptionRealizeTypeEnum.RealizedFaceToFace);
        setCurrencyCode('');
        setQuantityText('1');
        setUnitAmountText('0');
        setComments('');
    };

    // Arranca limpio en cada apertura
    useEffect(() => {
        if (props.isOpen) resetForm();
    }, [props.isOpen]);

    // Los protocolos del médico no traen la asociación de contrato: se pide el
    // protocolo completo para que los selectores de concepto y etapa tengan contrato.
    useEffect(() => {
        if (protocol?.id && protocol.contractAssociation === undefined) {
            props.GetAllProtocolContractAssociations(protocol.id);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [protocol?.id]);

    // Cierra cuando el guardado termina sin error
    useEffect(() => {
        if (wasSaving && !props.isSaving && !props.failSaving) {
            props.onSaved?.();
            props.onClose();
        }
        setWasSaving(props.isSaving);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isSaving]);

    const errors = validateDraft({
        protocolId: protocol?.id,
        conceptSelected: !!concept,
        serviceName,
        currencyCode,
        quantityText,
        unitAmountText,
        serviceDueDate
    });
    const isValid = !hasDraftErrors(errors);

    const onSubmit = () => {
        setSubmitted(true);
        if (!isValid || !protocol || !serviceDueDate) return;

        props.CreatePractitionerConsumption(buildConsumption({
            practitionerId: props.PractitionerId,
            practitionerName: props.Practitioner?.fullName || '',
            protocolId: protocol.id,
            protocolName: protocol.title || '',
            contractId,
            // La adenda sale del concepto o de la etapa elegidos; el protocolo no la conoce.
            addendumId: concept?.contractAddendumId ?? stage?.contractAddendumId,
            contractConceptId: concept?.id,
            conceptName: concept?.concept?.conceptName,
            stageId: stage?.id,
            stageName: stage?.stageName,
            stageInternalCrossVersionId: stage?.internalCrossVersionId,
            patientNumberInProtocol: patient?.numberInProtocol,
            serviceName,
            serviceDueDate,
            realizedType,
            currencyCode,
            quantity,
            unitAmount,
            comments
        }));
    };

    const showError = (message?: string) => (submitted && message ? message : undefined);

    return (
        <OverlayDrawer open={props.isOpen} position="end" size="medium" onOpenChange={(_, data) => { if (!data.open) props.onClose(); }} className="pcf-drawer">
            <DrawerHeader className="pcf-header">
                <DrawerHeaderTitle action={<Button appearance="subtle" aria-label="Cerrar" icon={<Dismiss24Regular />} onClick={props.onClose} />}>
                    <span className="pcf-eyebrow">Consumo manual</span>
                    <span className="pcf-title">Agregar consumo</span>
                </DrawerHeaderTitle>
                <span className="pcf-subtitle">{props.Practitioner?.fullName || 'Profesional'}</span>
            </DrawerHeader>

            <DrawerBody className="pcf-body">
                {props.failSaving && props.errorOnSaving && (
                    <div className="pcf-banner pcf-banner--error">
                        <Warning16Regular />
                        <div>
                            <strong>No se pudo guardar el consumo.</strong>
                            <div>{props.errorOnSaving.ErrorMessage}</div>
                            {props.errorOnSaving.Errors && props.errorOnSaving.Errors.length > 0 && (
                                <ul>{props.errorOnSaving.Errors.map((item) => <li key={item}>{item}</li>)}</ul>
                            )}
                        </div>
                    </div>
                )}

                {/* ── Protocolo ── */}
                <section className="pcf-section">
                    <h3 className="pcf-section__title">Protocolo</h3>

                    <Field label="Protocolo" required validationState={showError(errors.protocol) ? 'error' : 'none'} validationMessage={showError(errors.protocol)}>
                        <Dropdown
                            placeholder="Seleccioná un protocolo"
                            value={protocol?.title || ''}
                            selectedOptions={protocol ? [String(protocol.id)] : []}
                            onOptionSelect={(_, data) => {
                                const selected = props.Protocols.find((item) => String(item.id) === data.optionValue);
                                setProtocol(selected);
                                // lo elegido pertenecía al protocolo anterior
                                setPatient(undefined);
                                setStage(undefined);
                                setConcept(undefined);
                            }}
                        >
                            {props.Protocols.map((item) => (
                                <Option key={item.id} value={String(item.id)} text={item.title || `#${item.id}`}>
                                    {item.title || `#${item.id}`}
                                </Option>
                            ))}
                        </Dropdown>
                    </Field>

                    {protocol && (
                        <ProtocolPatientSelectorComponent
                            ProtocolId={protocol.id}
                            MultiselectEnable={false}
                            ProtocolPatientsSelectedNumber={patient ? [patient.numberInProtocol] : []}
                            onSelectedProtocolPatient={(patients: ProtocolPatient[]) => setPatient(patients[0])}
                        />
                    )}

                    {protocol && contractId === undefined && (
                        <div className="pcf-banner pcf-banner--warning">
                            <Warning16Regular />
                            <div>Este protocolo no tiene contrato asociado: no hay conceptos ni visitas para elegir. Podés cargar el consumo con el nombre del servicio.</div>
                        </div>
                    )}

                    {contractId !== undefined && (
                        <>
                            <ContractStagesSelectorComponent
                                ContractId={contractId}
                                MultiselectEnable={false}
                                ContractStagesSelected={stage ? [stage.id] : []}
                                onSelectedContractStage={(stages: ContractStage[]) => setStage(stages[0])}
                            />
                            <ContractConceptSelectorComponent
                                ContractId={contractId}
                                referralDate={serviceDueDate}
                                MultiselectEnable={false}
                                ContractConceptsSelected={concept ? [concept.id] : []}
                                onSelectedContractConcept={(concepts: ContractConcept[]) => setConcept(concepts[0])}
                            />
                        </>
                    )}
                </section>

                {/* ── Servicio ── */}
                <section className="pcf-section">
                    <h3 className="pcf-section__title">Servicio</h3>

                    <Field
                        label="Nombre del servicio"
                        required={!concept}
                        validationState={showError(errors.service) ? 'error' : 'none'}
                        validationMessage={showError(errors.service)}
                        hint={concept ? 'Opcional: si queda vacío se usa el nombre del concepto.' : undefined}
                    >
                        <Input value={serviceName} placeholder={concept?.concept?.conceptName || 'Descripción del servicio prestado'} onChange={(_, data) => setServiceName(data.value)} />
                    </Field>

                    <div className="pcf-row">
                        <DateTimeInput
                            label="Fecha del servicio"
                            placeHolder="Seleccioná la fecha"
                            selectedDate={serviceDueDate}
                            isSubmitted={submitted}
                            isRequired
                            errorMessage={showError(errors.dueDate)}
                            onSelectDate={(date: Date | undefined) => setServiceDueDate(date)}
                        />
                        <Field label="Modalidad">
                            <Dropdown
                                value={REALIZE_TYPE_OPTIONS.find((option) => option.key === realizedType)?.label || ''}
                                selectedOptions={[String(realizedType)]}
                                onOptionSelect={(_, data) => setRealizedType(Number(data.optionValue))}
                            >
                                {REALIZE_TYPE_OPTIONS.map((option) => (
                                    <Option key={option.key} value={String(option.key)} text={option.label}>{option.label}</Option>
                                ))}
                            </Dropdown>
                        </Field>
                    </div>
                </section>

                {/* ── Importe ── */}
                <section className="pcf-section">
                    <h3 className="pcf-section__title">Importe</h3>

                    <CurrencySelectorComponent
                        label="Moneda"
                        isRequired
                        defaultSelected={currencyCode}
                        errorMessage={showError(errors.currency)}
                        onCurrencyChange={(currency: string) => setCurrencyCode(currency)}
                    />

                    <div className="pcf-row">
                        <Field label="Cantidad" required validationState={showError(errors.quantity) ? 'error' : 'none'} validationMessage={showError(errors.quantity)} hint={!showError(errors.quantity) ? 'Entero mayor a cero.' : undefined}>
                            <Input type="text" inputMode="numeric" value={quantityText} onChange={(_, data) => setQuantityText(sanitizeIntegerText(data.value))} />
                        </Field>
                        <Field label="Valor unitario" required validationState={showError(errors.unitAmount) ? 'error' : 'none'} validationMessage={showError(errors.unitAmount)}>
                            <Input type="text" inputMode="decimal" contentAfter={currencyCode || undefined} value={unitAmountText} onChange={(_, data) => setUnitAmountText(sanitizeDecimalText(data.value))} />
                        </Field>
                    </div>

                    <div className="pcf-total">
                        <span className="pcf-total__label">Total</span>
                        <span className="pcf-total__value">{currencyCode ? formatCurrencyAmount(amount, currencyCode) : amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        <span className="pcf-total__hint">Queda pendiente de liquidar al médico.</span>
                    </div>
                </section>

                <section className="pcf-section">
                    <h3 className="pcf-section__title">Comentarios</h3>
                    <Field>
                        <Textarea value={comments} resize="vertical" placeholder="Notas sobre este consumo (opcional)" onChange={(_, data) => setComments(data.value)} />
                    </Field>
                </section>
            </DrawerBody>

            <DrawerFooter className="pcf-footer">
                <button type="button" className="pcf-btn" onClick={props.onClose} disabled={props.isSaving}>Cancelar</button>
                <button type="button" className="pcf-btn pcf-btn--primary" onClick={onSubmit} disabled={props.isSaving || (submitted && !isValid)}>
                    {props.isSaving && <Spinner size="extra-tiny" />} Agregar consumo
                </button>
            </DrawerFooter>
        </OverlayDrawer>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(PractitionerConsumptionFormComponent as any);
